import { ArrowBackIos } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import MuiLink from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { DealerRoutes } from '../../common/Routes';
import { selectUserRoles } from '../../redux/selectors/userDataSelectors';

const PREFIX = 'AppBreadCrumbs';

const classes = {
  link: `${PREFIX}-link`,
  linkActive: `${PREFIX}-linkActive`,
  textButton: `${PREFIX}-textButton`,
  list: `${PREFIX}-list`,
};

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  [`&.${classes.list}`]: {
    '& .MuiBreadcrumbs-ol': {
      alignItems: 'baseline',
      '& .MuiBreadcrumbs-separator': {
        fontSize: theme.typography.caption.fontSize,
      },
    },
  },
  [`& .${classes.link}`]: {
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  [`& .${classes.linkActive}`]: {
    color: theme.palette.info.dark,
    textDecoration: 'none',
    lineHeight: '1.5',
  },
}));

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.textButton}`]: {
    textTransform: 'capitalize',
    color: theme.palette.info.dark,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.body2.fontWeight,
  },
}));

const AppBreadCrumbs = () => {
  const userRoles = useSelector(selectUserRoles);
  const { pathname } = useLocation();
  const routes = DealerRoutes(userRoles);
  const matchRoute = path => matchPath(pathname, { path });
  const getPrevAndCurrentRoutes = object => {
    const currentRoutes = {};
    let key = 0;
    for (const prop in object) {
      const matched = matchRoute(routes[prop].path);
      if (matched) {
        const currentObj = { ...object[prop] };
        currentObj.isExact = matched.isExact;
        currentRoutes[key] = currentObj;
        key++;
      }
    }
    const currentExactKey = Object.keys(currentRoutes).find(
      k => currentRoutes[k].isExact === true
    );
    return {
      prev: currentRoutes[currentExactKey - 1],
      current: currentRoutes[currentExactKey],
    };
  };
  const currentAndPrev = getPrevAndCurrentRoutes(routes);
  const currentURL = currentAndPrev.current.path;

  const componentByIsExact = {
    true: Typography,
    false: Link,
  };

  return (
    currentURL !== '/' && (
      <>
        <StyledBreadcrumbs
          aria-label="breadcrumb"
          data-test="breadcrumb"
          sx={{ display: { xs: 'none', lg: 'block' } }}
          className={classes.list}
        >
          {routes.map((r, i) => {
            const state = matchRoute(r.path);
            return (
              state &&
              r.breadCrumb && (
                <MuiLink
                  key={i}
                  component={componentByIsExact[state.isExact]}
                  to={{
                    pathname: r.path,
                  }}
                  color="inherit"
                  variant="caption"
                  className={state.isExact ? classes.linkActive : classes.link}
                >
                  {`${r.breadCrumb}`}
                </MuiLink>
              )
            );
          })}
        </StyledBreadcrumbs>
        <StyledBox
          aria-label="breadcrumb"
          sx={{ display: { xs: 'block', lg: 'none' } }}
        >
          <Button
            component={Link}
            to={{
              pathname: currentAndPrev.prev.path,
            }}
            variant="text"
            startIcon={<ArrowBackIos />}
            className={classes.textButton}
          >
            {`${currentAndPrev.prev.breadCrumb}`}
          </Button>
        </StyledBox>
      </>
    )
  );
};

export default AppBreadCrumbs;
